/**
 * Utility functions for detecting changes in form data for PATCH requests.
 * Only sends fields that have actually changed, following REST PATCH semantics.
 */

/**
 * Checks if two values are different, with support for deep array comparison
 */
const hasChanged = <T>(newVal: T, oldVal: T, deepCompare = false): boolean => {
  if (deepCompare && Array.isArray(newVal) && Array.isArray(oldVal)) {
    return JSON.stringify(newVal) !== JSON.stringify(oldVal);
  }
  return newVal !== oldVal;
};

/**
 * Conditionally adds a field to the changed fields object if it has changed
 */
const addIfChanged = <T>(
  fields: Record<string, unknown>,
  key: string,
  newVal: T,
  oldVal: T,
  deepCompare = false
): void => {
  if (hasChanged(newVal, oldVal, deepCompare)) {
    fields[key] = newVal;
  }
};

/**
 * Builds an object containing only the fields that have changed between two objects.
 * Useful for PATCH requests where only modified fields should be sent.
 *
 * @param fields - Array of field configurations to check
 * @returns Object containing only changed fields
 *
 * @example
 * ```typescript
 * const changedFields = buildChangedFields([
 *   { key: 'title', newValue: formData.title, oldValue: initialValues.title },
 *   { key: 'assignees', newValue: formData.assignees, oldValue: initialValues.assignees, deepCompare: true },
 * ]);
 * ```
 */
export const buildChangedFields = (
  fields: Array<{
    key: string;
    newValue: unknown;
    oldValue: unknown;
    deepCompare?: boolean;
  }>
): Record<string, unknown> => {
  const changedFields: Record<string, unknown> = {};

  for (const { key, newValue, oldValue, deepCompare = false } of fields) {
    addIfChanged(changedFields, key, newValue, oldValue, deepCompare);
  }

  return changedFields;
};
